import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const MODE = { phone: "Phone", in_person: "In person", video: "Video" };
const dayKey = (iso) => new Date(iso).toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "short", month: "short", day: "numeric" });
const hm = (iso) => new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });

export const slotLabel = (iso) => `${dayKey(iso)}, ${hm(iso)}`;

export const BookCheckin = ({ open, setOpen, teacherId, reschedule, onDone, preset }) => {
  const [d, setD] = useState(null);
  const [pick, setPick] = useState(null);
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setPick(null); setD(null);
    http.get(`/conferences/slots/${teacherId}`, { params: reschedule ? { exclude: reschedule.id } : {} }).then((r) => {
      setD(r.data);
      const p = preset && r.data.slots.find((s) => s.start === preset);
      if (preset && !p) toast.error("That time was just taken. Please pick another.");
      setPick(p || null);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [open, teacherId, reschedule, preset]);
  const days = useMemo(() => (d?.slots || []).reduce((a, s) => ({ ...a, [dayKey(s.start)]: [...(a[dayKey(s.start)] || []), s] }), {}), [d]);
  const submit = async () => {
    setBusy(true);
    try {
      if (reschedule) await http.post(`/conferences/${reschedule.id}/reschedule`, { start: pick.start, phone: phone || null });
      else await http.post("/conferences", { teacher_id: teacherId, start: pick.start, phone: phone || null, note: note || null });
      toast.success(reschedule ? "Check-in moved" : "Check-in booked"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#FDFBF7] max-w-xl max-h-[88vh] overflow-y-auto" data-testid="book-dialog">
        <DialogTitle className="font-display text-2xl text-[#2C4A3E]">{reschedule ? "Pick a new time" : "Book a check-in"}</DialogTitle>
        {d && <p className="text-xs text-stone-500">{d.duration}-minute check-ins · Eastern Time · at least 24 hours ahead</p>}
        {d && !d.slots.length && <p className="text-sm text-stone-500 py-6" data-testid="book-no-slots">No open times right now. Send a message to ask for a time.</p>}
        <div className="space-y-4">{Object.entries(days).map(([day, list]) => (
          <div key={day}><p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-2">{day}</p>
            <div className="flex flex-wrap gap-2">{list.map((s) => (
              <button key={s.start} onClick={() => setPick(s)} data-testid={`slot-${s.start}`}
                className={`px-3 py-2 rounded-full border text-sm ${pick?.start === s.start ? "bg-[#2C4A3E] text-white border-[#2C4A3E]" : "bg-white border-[#E6DEC3] text-[#2C4A3E] hover:bg-[#F1EADB]"}`}>
                {hm(s.start)} <span className="opacity-60 text-xs">· {MODE[s.mode]}</span></button>))}</div>
          </div>))}</div>
        {pick && (
          <div className="space-y-3 pt-3 pb-1 border-t border-[#E6DEC3] sticky -bottom-6 bg-[#FDFBF7]" data-testid="book-pick-panel">
            {pick.mode === "phone" && !reschedule?.phone && <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your phone number for the call" data-testid="book-phone"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E6DEC3] text-sm" />}
            {pick.mode === "in_person" && d.location && <p className="text-xs text-stone-600" data-testid="book-location">Where: {d.location}</p>}
            {pick.mode === "video" && <p className="text-xs text-stone-600">The teacher will add a private video link before your check-in.</p>}
            {!reschedule && <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} rows={2} placeholder="What would you like to talk about? (optional)" data-testid="book-note"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E6DEC3] text-sm" />}
            <button onClick={submit} disabled={busy} data-testid="book-confirm" className="w-full py-3 rounded-full bg-[#C87D53] text-white text-sm disabled:opacity-50">
              {reschedule ? "Move to" : "Book"} {dayKey(pick.start)} at {hm(pick.start)}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
